import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TASK_CATEGORIES, ACTION_LABELS } from './routeTaskCatalog';

// Per-stop selectable task checklist + location notes.
// `tasks` is an array of { key, label, category, action }. `onChange(tasks)` reports edits.
// Nothing is preselected. Selecting a work task defaults its action to "inspect" (never
// "perform") so selection alone never represents work as approved.
const RouteStopTasks = ({ stopId, tasks = [], notes = '', onTasksChange, onNotesChange }) => {
  const [open, setOpen] = useState({});
  const byKey = Object.fromEntries(tasks.map(t => [t.key, t]));
  const selectedCount = (catKey) => TASK_CATEGORIES.find(c => c.key === catKey).tasks.filter(t => byKey[t.key]).length;

  const toggleTask = (cat, t) => {
    if (byKey[t.key]) onTasksChange(tasks.filter(x => x.key !== t.key));
    else onTasksChange([...tasks, { key: t.key, label: t.label, category: cat.label, action: t.mode === 'observe' ? 'observe' : 'inspect' }]);
  };
  const setAction = (key, action) => onTasksChange(tasks.map(x => x.key === key ? { ...x, action } : x));

  return (
    <div className="mt-2" data-testid={`rp-tasks-${stopId}`}>
      <div className="mb-1 text-xs font-semibold text-slate-500">Visit tasks <span className="font-normal text-slate-400">({tasks.length} selected · optional)</span></div>
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {TASK_CATEGORIES.map(cat => {
          const n = selectedCount(cat.key);
          const isOpen = open[cat.key];
          return (
            <div key={cat.key}>
              <button type="button" onClick={() => setOpen(o => ({ ...o, [cat.key]: !o[cat.key] }))} data-testid={`rp-cat-${stopId}-${cat.key}`} className="flex w-full items-center justify-between px-2.5 py-2 text-left text-sm hover:bg-slate-50">
                <span className="flex items-center gap-1.5 font-medium text-[#0E2A47]">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {cat.label}</span>
                {n > 0 && <span className="rounded-full bg-[#5FA718] px-2 py-0.5 text-[10px] font-bold text-white">{n}</span>}
              </button>
              {isOpen && (
                <div className="space-y-1.5 px-3 pb-2.5 pt-0.5">
                  {cat.tasks.map(t => {
                    const sel = byKey[t.key];
                    return (
                      <div key={t.key} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <label className="flex min-w-0 flex-1 items-start gap-2 text-sm text-slate-700">
                          <input type="checkbox" checked={!!sel} onChange={() => toggleTask(cat, t)} data-testid={`rp-task-${stopId}-${t.key}`} className="mt-0.5" />
                          <span className={sel ? 'font-medium text-[#0E2A47]' : ''}>{t.label}</span>
                        </label>
                        {sel && (t.mode === 'observe'
                          ? <span className="ml-6 shrink-0 rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500 sm:ml-0">{ACTION_LABELS.observe}</span>
                          : <select value={sel.action} onChange={e => setAction(t.key, e.target.value)} data-testid={`rp-action-${stopId}-${t.key}`} className="ml-6 shrink-0 rounded border border-slate-200 px-2 py-1 text-xs sm:ml-0">
                              <option value="inspect">{ACTION_LABELS.inspect}</option>
                              <option value="perform">{ACTION_LABELS.perform}</option>
                              <option value="both">{ACTION_LABELS.both}</option>
                            </select>)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <textarea value={notes} onChange={e => onNotesChange(e.target.value)} placeholder="Location-specific notes (applies only to this stop)" rows={2} data-testid={`rp-notes-${stopId}`} className="mt-2 w-full rounded border border-slate-200 px-2 py-1.5 text-sm" />
    </div>
  );
};

export default RouteStopTasks;
